import { useMemo } from "react";
import { useFields } from "../../hooks/queries";
import { useFieldToolsStore } from "../../stores/fieldToolsStore";
import {
  CombineFieldsButton,
  DeleteFieldsButton,
} from "../../components/FieldActionsBar";
import { AddFieldMenu } from "../add-field/AddFieldMenu";
import { NewFolderButton } from "../new-folder/NewFolderButton";

// THE HEADER TOOLBAR, in three slots: [logo] [ Add Field/Data | New folder | Delete ]
// ················· [ Filter fields… ] [profile] Visualize sits with Map Soil / Build Rx / Spread
// Fertilizer / File Section 180 in `FieldActionsRow` above the table — the same filled buttons. The
// header keeps only the occasional items.
function useSelectedFields() {
  const { data } = useFields();
  const rowSelection = useFieldToolsStore((s) => s.rowSelection);
  return useMemo(
    () => (data ?? []).filter((f) => rowSelection[f.field_id]),
    [data, rowSelection],
  );
}

/*
 *
 * LEFT, beside the logo: a plain website-style menu — no outlined buttons, just labels with hairline
 * dividers between them. Order is left to right: Add Field/Data · New folder · Delete.
 *
 * Visualize is not here. It is the first filled button in `FieldActionsRow`, beside Map Soil.
 */
export function FieldToolsMenu() {
  const selected = useSelectedFields();
  const items = [
    <AddFieldMenu key="add" />,
    <NewFolderButton key="folder" />,
    // renders NOTHING below two selected fields, so the menu doesn't grow a permanently-disabled item
    <CombineFieldsButton key="combine" selected={selected} />,
    <DeleteFieldsButton key="del" selected={selected} />,
  ].filter((el) => el.type !== CombineFieldsButton || selected.length > 1);
  return (
    <nav className="flex items-center">
      {items.map((el, i) => (
        <div key={el.key} className="flex items-center">
          {i > 0 && (
            <span className="mx-0.5 h-4 w-px shrink-0 bg-line" aria-hidden />
          )}
          {el}
        </div>
      ))}
    </nav>
  );
}

/* * RIGHT, immediately left of the profile menu. */
export function FieldToolsFilter() {
  const query = useFieldToolsStore((s) => s.query);
  const setQuery = useFieldToolsStore((s) => s.setQuery);
  return (
    <input
      className="w-36 rounded border border-line bg-canvas px-2.5 py-1.5 text-[12px] text-fg placeholder-faint outline-none focus:border-muted lg:w-48"
      placeholder="Filter fields…"
      aria-label="Filter fields"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
    />
  );
}
